import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { StatusPill } from "@/components/jobs/StatusBox";

export default function StatusesCard() {
  const [list, setList] = useState(null);
  const [name, setName] = useState("");

  useEffect(() => {
    api.get("/job-statuses").then((r) => setList(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!list) return null;
  const set = (i, v) => setList(list.map((s, j) => (j === i ? v : s)));
  const move = (i, d) => {
    const next = [...list];
    [next[i], next[i + d]] = [next[i + d], next[i]];
    setList(next);
  };
  const add = (e) => {
    e.preventDefault();
    if (name.trim()) setList([...list, name.trim()]);
    setName("");
  };
  const save = async () => {
    try {
      setList((await api.put("/job-statuses", { statuses: list })).data);
      toast.success("Job statuses saved");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <section className="max-w-3xl rounded-2xl border border-[#103080]/10 bg-white/70 ambient p-6 sm:p-8" data-testid="statuses-card">
      <h2 className="font-serif-brand font-semibold text-2xl text-[#103080]">Job statuses</h2>
      <p className="text-sm text-[#0F172A]/60 mt-1 mb-5">The statuses a job card can have. In a template, set a task to change the status when it's completed. A card moves to <b>In progress</b> once work starts and <b>Complete</b> when every task is done.</p>
      <ul className="space-y-2">
        {list.map((s, i) => (
          <li key={i} className="flex items-center gap-2" data-testid={`status-row-${i}`}>
            <Input value={s} onChange={(e) => set(i, e.target.value)} className="rounded-lg bg-white border-[#103080]/20 h-10 flex-1" data-testid={`status-input-${i}`} />
            <span className="w-40 hidden sm:block"><StatusPill status={s} /></span>
            <button disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" className="text-[#103080]/40 hover:text-[#103080] disabled:opacity-30"><ArrowUp size={15} /></button>
            <button disabled={i === list.length - 1} onClick={() => move(i, 1)} aria-label="Move down" className="text-[#103080]/40 hover:text-[#103080] disabled:opacity-30"><ArrowDown size={15} /></button>
            <button onClick={() => setList(list.filter((_, j) => j !== i))} aria-label="Remove" className="text-red-500 hover:text-red-700" data-testid={`status-remove-${i}`}><Trash2 size={15} /></button>
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="flex gap-2 mt-4">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New status, e.g. With HMRC" className="rounded-lg bg-white border-[#103080]/20 h-10 flex-1" data-testid="status-new-input" />
        <button type="submit" data-testid="status-add-button" className="inline-flex items-center gap-1.5 rounded-full border border-[#103080]/20 px-4 h-10 text-sm font-medium text-[#103080] hover:border-[#0090D0] transition-colors duration-200"><Plus size={14} /> Add</button>
      </form>
      <button onClick={save} data-testid="statuses-save" className="mt-5 rounded-full bg-[#103080] text-white font-medium px-7 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200">Save statuses</button>
    </section>
  );
}
